import React, { FC } from 'react';

import { Control } from 'react-hook-form';

import AcSelect from 'components/AcSelect/AcSelect';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import { FormControl, FormField, FormItem } from 'components/ui';

import {
  EPopupTriggersAction,
  PopupsFormFields
} from '../../PopupsForm.consts';
import { Chip } from '@appcharge/shared-ui';

import { createNumberInputHandlers } from '../../PopupsForm.utils';

const CustomChip = (): React.ReactElement => (
  <div className="w-full mt-2.5 justify-center flex items-center">
    <div className="h-1.5 w-1.5 bg-indigo-600 rounded-full" />
    <div className="w-6 border-[1px] border-indigo-600 h-[1px] border-dashed mx-0.5" />
    <Chip className=" bg-violet-100 text-blue-950">And</Chip>
    <div className="w-6 border-[1px] border-indigo-600 h-[1px] border-dashed mx-0.5" />
    <div className="h-1.5 w-1.5 bg-indigo-600 rounded-full" />
  </div>
);

const TRIGGER_ACTION_OPTIONS = [
  {
    value: EPopupTriggersAction.PURCHASE_COMPLETED,
    label: 'Purchase completed'
  },
  {
    value: EPopupTriggersAction.STORE_REFRESH,
    label: 'Store refresh'
  }
];

interface ITriggersSectionProps {
  control: Control<PopupsFormFields>;
  offersIds: any;
  isTriggeredPopups?: boolean;
  triggersAction?: EPopupTriggersAction;
  onTriggersActionChange?: (action: EPopupTriggersAction) => void;
}

const TriggersSection: FC<ITriggersSectionProps> = ({
  control,
  offersIds,
  isTriggeredPopups = true,
  triggersAction,
  onTriggersActionChange
}) => {
  const isStoreRefresh = triggersAction === EPopupTriggersAction.STORE_REFRESH;
  const renderIdValue = (selected: string[]): React.ReactNode => {
    const selectedLength = selected?.length;
    if (selectedLength === 0) return 'Select';
    if (selectedLength < offersIds?.length && selectedLength > 1)
      return `${selected[0]} and ${selectedLength - 1} more...`;
    if (selectedLength === 1) return selected[0];
    return 'All';
  };

  let actionOptions = [TRIGGER_ACTION_OPTIONS[0]];
  if (isTriggeredPopups) {
    actionOptions = TRIGGER_ACTION_OPTIONS;
  }

  return (
    <div>
      <RichText
        title="Triggers"
        description="Define when the offer will be presented to the player"
        className="mb-5"
      />
      <FormField
        control={control}
        name="triggersAction"
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <InputSelectDropdown
                isRequired
                key={value}
                label="Action"
                defaultSelected={actionOptions.find(
                  (opt) => opt.value === value
                )}
                options={actionOptions}
                onOptionChange={(newValue: string): void => {
                  onChange(newValue);
                  onTriggersActionChange?.(newValue as EPopupTriggersAction);
                }}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <CustomChip />
      <FormField
        control={control}
        name="triggersFrequency"
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <Input
                min={1}
                label="Frequency"
                type="number"
                data-testid="triggers-frequency-input"
                value={value}
                tooltipIcon="fa-circle-exclamation"
                infoTooltipContent="Frequency controls how often the popup appears when its conditions are met"
                {...createNumberInputHandlers(onChange, 1)}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <CustomChip />
      {isStoreRefresh && (
        <FormField
          control={control}
          name="coolDownInMinutes"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <Input
                  label="Cooldown"
                  type="number"
                  min={1}
                  value={value ?? ''}
                  placeholder="1 Min"
                  tooltipIcon="fa-circle-exclamation"
                  infoTooltipContent="Cooldown (in minutes) specifies the minimum time between consecutive displays of the offer."
                  {...createNumberInputHandlers(onChange, 1)}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
      {!isStoreRefresh && (
        <FormField
          control={control}
          name="triggersOfferExternalIds"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <AcSelect
                  required
                  key={value?.join(',') ?? ''}
                  header="Offer ID"
                  items={offersIds}
                  isCheckBox
                  setSelectedItems={onChange}
                  value={value || []}
                  isSelectAllEnabled={true}
                  isApplyBtnEnabled
                  renderValue={(value: unknown) =>
                    renderIdValue(value as string[])
                  }
                  dataTestValidationId="popupExternalIdsValidationTxt"
                  dataTestListId="dashPopupExternalIdsList"
                  dataTestBoxId="popupExternalIdsBox"
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </div>
  );
};

export { TriggersSection };
